import {
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  updatePassword,
  sendPasswordResetEmail,
  User
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { AdminUser } from '../types';

export const signIn = async (email: string, password: string): Promise<AdminUser> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  const adminDoc = await getDoc(doc(db, 'admins', user.uid));

  if (!adminDoc.exists()) {
    await firebaseSignOut(auth);
    throw new Error('Access denied. You are not authorized as an admin.');
  }

  await setDoc(doc(db, 'admins', user.uid), {
    lastLogin: serverTimestamp()
  }, { merge: true });

  const adminData = adminDoc.data();
  return {
    uid: user.uid,
    email: user.email || '',
    displayName: adminData.displayName || user.email || '',
    role: adminData.role || 'admin',
    createdAt: adminData.createdAt?.toDate() || new Date(),
    lastLogin: new Date()
  };
};

export const signOut = async (): Promise<void> => {
  await firebaseSignOut(auth);
};

export const onAuthStateChanged = (callback: (user: User | null) => void): (() => void) => {
  return firebaseOnAuthStateChanged(auth, callback);
};

export const getCurrentAdmin = async (user: User): Promise<AdminUser | null> => {
  const adminDoc = await getDoc(doc(db, 'admins', user.uid));

  if (!adminDoc.exists()) {
    return null;
  }

  const adminData = adminDoc.data();
  return {
    uid: user.uid,
    email: user.email || '',
    displayName: adminData.displayName || user.email || '',
    role: adminData.role || 'admin',
    createdAt: adminData.createdAt?.toDate() || new Date(),
    lastLogin: adminData.lastLogin?.toDate() || new Date()
  };
};

export const changePassword = async (newPassword: string): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('No user is currently signed in.');
  }
  await updatePassword(user, newPassword);
};

export const resetPassword = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

// ================== RETAILER AUTH ==================

export interface RetailerUser {
  uid: string;
  email: string;
  businessName: string;
  contactName: string;
  retailerId: string;
}

export const signInRetailer = async (email: string, password: string): Promise<RetailerUser> => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  const adminDoc = await getDoc(doc(db, 'admins', user.uid));
  if (adminDoc.exists()) {
    await firebaseSignOut(auth);
    throw new Error('Please use the admin portal to sign in.');
  }

  const { getDocs, query, collection, where } = await import('firebase/firestore');
  const retailersQuery = query(
    collection(db, 'retailers'),
    where('email', '==', email)
  );
  const retailerSnapshot = await getDocs(retailersQuery);

  if (retailerSnapshot.empty) {
    await firebaseSignOut(auth);
    throw new Error('No retailer account found. Please contact support.');
  }

  const retailerDoc = retailerSnapshot.docs[0];
  const retailerData = retailerDoc.data();

  if (retailerData.status === 'suspended') {
    await firebaseSignOut(auth);
    throw new Error('Your account has been suspended. Please contact support.');
  }

  return {
    uid: user.uid,
    email: user.email || '',
    businessName: retailerData.businessName,
    contactName: retailerData.contactName,
    retailerId: retailerDoc.id
  };
};

export const getCurrentRetailer = async (user: User): Promise<RetailerUser | null> => {
  const adminDoc = await getDoc(doc(db, 'admins', user.uid));
  if (adminDoc.exists()) {
    return null;
  }

  const { getDocs, query, collection, where } = await import('firebase/firestore');
  const retailersQuery = query(
    collection(db, 'retailers'),
    where('email', '==', user.email)
  );
  const retailerSnapshot = await getDocs(retailersQuery);

  if (retailerSnapshot.empty) {
    return null;
  }

  const retailerDoc = retailerSnapshot.docs[0];
  const retailerData = retailerDoc.data();

  if (retailerData.status === 'suspended') {
    return null;
  }

  return {
    uid: user.uid,
    email: user.email || '',
    businessName: retailerData.businessName,
    contactName: retailerData.contactName,
    retailerId: retailerDoc.id
  };
};

export const signOutRetailer = async (): Promise<void> => {
  await firebaseSignOut(auth);
};
